/**
 * Definição de senha a partir de um link de uso único — dois modos:
 *
 *  - `redefinir` (rota /redefinir-senha): recuperação de acesso;
 *  - `convite`   (rota /convite): primeiro acesso de um usuário convidado
 *    (CERES-005). O mesmo endpoint confirma os dois — a diferença é o texto,
 *    porque para quem chega pela primeira vez "redefinir" não descreve nada.
 *
 * O link só é emitido quando há entrega real de e-mail (MAILER_DRIVER=smtp) ou
 * quando o administrador o recebeu na criação/reenvio do convite para entrega
 * por canal seguro (BKC-006: nada aqui promete e-mail que não sai).
 */

import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { BRANDING } from '../config/branding';
import { api } from '../lib/api';
import { t } from '../lib/i18n';
import { Field, InlineError, Spinner, useToast,
  FormErrorSummary,
} from '../components/ui';

export function PasswordResetPage({ modo = 'redefinir' }: { modo?: 'redefinir' | 'convite' }): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { notify } = useToast();

  const [token, setToken] = useState(searchParams.get('token') ?? '');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const convite = modo === 'convite';
  const mismatch = confirmation !== '' && password !== confirmation;

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (mismatch) return;

    setError(null);
    setBusy(true);
    try {
      await api.post('/auth/password-reset/confirm', { token, password });
      notify('success', convite ? t('convite.sucesso') : 'Senha redefinida. Entre com a nova senha.');
      navigate('/entrar', { replace: true });
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-sunken px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <h1 className="text-xl font-semibold text-ink">
            {convite ? t('convite.titulo') : t('redefinir.titulo')}
          </h1>
          <p className="mt-0.5 text-sm text-ink-muted">
            {convite ? t('convite.texto') : t('redefinir.texto')}
          </p>
        </div>

        <div className="panel px-5 py-5">
          <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4" noValidate>
            <Field
              label={t('redefinir.token')}
              htmlFor="token"
              required
              hint={convite ? t('convite.tokenDica') : t('redefinir.tokenDica')}
            >
              <input
                id="token"
                className="field-input font-mono text-xs"
                value={token}
                onChange={(event) => setToken(event.target.value)}
                required
              />
            </Field>

            <Field
              label={convite ? t('convite.escolhaSenha') : t('redefinir.novaSenha')}
              htmlFor="password"
              required
              hint="Mínimo de 10 caracteres."
            >
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                minLength={10}
                className="field-input"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </Field>

            <Field
              label={t('redefinir.confirmeNova')}
              htmlFor="confirmation"
              required
              error={mismatch ? 'As senhas não coincidem.' : undefined}
            >
              <input
                id="confirmation"
                type="password"
                autoComplete="new-password"
                className={`field-input ${mismatch ? 'field-input-invalid' : ''}`}
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                required
              />
            </Field>

            <FormErrorSummary error={error} />
        <InlineError error={error} />

            <button
              type="submit"
              className="btn-primary w-full"
              disabled={busy || mismatch || token === '' || password.length < 10}
            >
              {busy && <Spinner />}
              {convite ? t('convite.botao') : 'Redefinir senha'}
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-2xs text-ink-subtle">
          <Link to="/entrar" className="underline hover:text-ink-muted">{t('redefinir.voltarLogin')}</Link>
          {' · '}
          {BRANDING.legalLine}
        </p>
      </div>
    </div>
  );
}
